<script lang="ts" setup>
    import { computed, nextTick, onMounted, reactive } from "vue";
    import { useAuthStore } from "@/stores/authStore";
    import { FormInput } from "@/base-components/Form";
    import AlertForgeBuild from "@/base-components/Alert/AlertForgeBuild.vue";
    import illustrationUrl from "@/assets/images/illustration.svg";
    import logo from "@/assets/images/rotated_logo.svg";
    import LoadingIcon from "@/base-components/LoadingIcon";
    import Lucide from "@/base-components/Lucide";
    import BaseButton from "../base-components/Button/BaseButton.vue";
    import LoginLinks from "@/pages/components/LoginLinks.vue";
    import { logger } from '../utils/logger';

    const AuthStore = useAuthStore();

    interface Data {
        emailChecker: string;
        invalidEmailChecker: boolean;
        invalidOfficeAuth: boolean;
        invalidOfficeUser: boolean;
        invalidPassword: boolean;
        emailType: string;
        isLogginIn: boolean;
        twoFactorAuthentication: boolean;
        flash: string;
        flash_message: string;
        email: string;
        password: string;
        error: any;
        code: string;
        invalidLogin: boolean;
        twoFactorValidation: {
            error: boolean;
            message: string;
        };
    }

    const data: Data = reactive({
        emailChecker: "",
        invalidEmailChecker: false,
        invalidOfficeAuth: false,
        invalidOfficeUser: false,
        invalidPassword: false,
        emailType: "",
        isLogginIn: false,
        twoFactorAuthentication: false,
        flash: "",
        flash_message: "",
        email: "",
        password: "",
        error: null,
        code: "",
        invalidLogin: false,
        twoFactorValidation: {
            error: false,
            message: ""
        }
    });

    onMounted(() => {
        nextTick(() => {
            const emailInput =
                document.getElementById("emailChecker");
            if (emailInput) {
                emailInput.focus();
            }
        });
        if ((window as any).Laravel?.flash_message !== null) {
            data.flash = (window as any).Laravel?.flash_error;
            data.flash_message = (
                window as any
            ).Laravel?.flash_message;
        }

        const params = new URLSearchParams(window.location.search);
        const redirectTo = params.get('redirect_to');
        if (redirectTo && isValidInternalPath(redirectTo)) {
            sessionStorage.setItem('redirectUrl', redirectTo);
        }
    });

    const isValidInternalPath = (path: string) => {
        return path.startsWith(import.meta.env.VITE_APP_URL) || path.startsWith('/');
    };

    const handleEmailInput = (event: {
        target: { value: any };
    }) => {
        //console.log(event.target.value);
        validateEmail(event.target.value);
    };

    const validateEmail = (email: string) => {
        if (
            /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(
                email
            )
        ) {
            logger.debug("valid email");
            return true;
        } else {
            logger.warn("invalid email");
            return false;
        }
    };

    const checkEmail = () => {
        data.invalidLogin = false;
        if (data.emailChecker === "" || !validateEmail(data.emailChecker)) {
            data.invalidEmailChecker = true;
            nextTick(() => {
                const emailInput =
                    document.getElementById("emailChecker");
                if (emailInput) {
                    emailInput.focus();
                }
            });
        } else {
            const email = data.emailChecker.split("@").pop();
            data.invalidEmailChecker = false;
            if (email === "remoteemployee.com") {
                window.location.href =
                    import.meta.env.VITE_APP_URL +
                    "/signin/" +
                    data.emailChecker;
            } else {
                data.emailType = "client";
                data.email = data.emailChecker;
                data.invalidPassword = false;
                data.invalidLogin = false;
                nextTick(() => {
                    const passwordInput =
                        document.getElementById("password");
                    if (passwordInput) {
                        passwordInput.focus();
                    }
                });
            }
        }
    };

    const changeEmailAddress = () => {
        data.emailType = "";
        data.email = "";
        data.password = "";
        nextTick(() => {
            const emailInput =
                document.getElementById("emailChecker");
            if (emailInput) {
                emailInput.focus();
            }
        });
    };

    const handleSubmit = async (e: {
        preventDefault: () => void;
    }) => {
        logger.info("login");
        e.preventDefault();
        const AuthStore = useAuthStore();
        if (data.password.length > 0) {
            data.invalidPassword = false;
            await AuthStore.login(data.email, data.password)
                .then((response) => {
                    logger.debug(response);
                    if (
                        response &&
                        response.status === "success"
                    ) {
                        logger.info("login success");
                        logger.debug(response.data);

                        const redirectUrl =
                            sessionStorage.getItem(
                                "redirectUrl"
                            ) || "/";
                        sessionStorage.removeItem(
                            "redirectUrl"
                        );
                        window.location.replace(
                            import.meta.env.VITE_APP_URL +
                                redirectUrl
                        );
                    } else if (
                        response &&
                        response.status ===
                            "two_factor_required"
                    ) {
                        logger.info(
                            "two-factor authentication required"
                        );
                        // Rest of the code for 2FA login
                    } else {
                        logger.error("login error");
                        logger.error(response?.error);
                        data.invalidLogin = true;
                        // Rest of the code for login error
                    }
                })
                .catch((error) => {
                    logger.error("login error");
                    logger.error(error);
                    data.invalidLogin = true;
                    // Rest of the code for login error
                });
        } else {
            data.invalidPassword = true;
            nextTick(() => {
                const passwordInput =
                    document.getElementById("password");
                if (passwordInput) {
                    passwordInput.focus();
                }
            });
        }
    };

    const isLocal = computed(() => {
        return import.meta.env.VITE_APP_ENV === "local";
    });
</script>

<template>
    <div
        :class="[
            `relative flex h-screen items-center bg-slate-100
            dark:bg-darkmode-800 sm:-mx-8 sm:px-8 lg:overflow-hidden
            xl:bg-white xl:dark:bg-darkmode-600`,
            `before:absolute before:inset-y-0 before:left-0
            before:-mb-[16%] before:-ml-[13%] before:-mt-[28%]
            before:hidden before:w-[57%] before:rotate-[-4.5deg]
            before:transform before:rounded-[100%]
            before:bg-custom-color-1000/20 before:content-[\'\']
            before:dark:bg-darkmode-400 before:xl:block`,
            `after:absolute after:inset-y-0 after:left-0 after:-mb-[13%]
            after:-ml-[13%] after:-mt-[20%] after:hidden after:w-[57%]
            after:rotate-[-4.5deg] after:transform after:rounded-[100%]
            after:bg-custom-color-1000 after:content-[\'\']
            after:dark:bg-darkmode-700 after:xl:block`
        ]"
    >
        <div
            class="absolute top-0 w-full h-1/2 bg-login-gradient xl:hidden"
        ></div>
        <!-- <DarkModeSwitcher/> -->
        <!-- <MainColorSwitcher/> -->
        <AlertForgeBuild
            :location="'public'"
            class="absolute left-1/2 top-0 z-20 min-w-[350px] -translate-x-1/2"
        />
        <div class="container relative z-10 sm:px-10">
            <div class="block grid-cols-2 gap-4 xl:grid">
                <!-- BEGIN: Login Info -->
                <div class="flex-col hidden h-screen xl:flex">
                    <span class="flex items-center pt-5 -intro-x">
                        <img
                            alt="RE Portal"
                            class="w-6"
                            :src="logo"
                        />
                        <span class="ml-3 text-lg text-white">
                            Remote Employee Portal
                        </span>
                    </span>
                    <div class="my-auto">
                        <img
                            :src="illustrationUrl"
                            alt="RE Portal"
                            class="w-1/2 -mt-16 -intro-x"
                        />
                        <div
                            class="mt-10 text-4xl font-medium leading-tight text-white -intro-x"
                        >
                            A few more clicks to
                            <br />
                            sign in to your account.
                        </div>
                        <div
                            class="mt-5 text-lg text-white -intro-x text-opacity-70 dark:text-slate-400"
                        >
                            &nbsp;&nbsp;
                        </div>
                    </div>
                </div>
                <!-- END: Login Info -->
                <!-- BEGIN: Login Form -->
                <form
                    @submit.prevent="handleSubmit"
                    class="flex justify-center xl:my-0 xl:h-auto xl:py-0"
                >
                    <!-- Email Form -->
                    <div
                        v-if="data.emailType === ''"
                        class="w-full px-5 py-16 mx-8 my-auto bg-white rounded-md shadow-lg dark:bg-darkmode-600 sm:w-3/4 sm:px-8 lg:w-2/4 xl:ml-20 xl:w-auto xl:bg-transparent xl:p-0 xl:shadow-none"
                    >
                        <div class="space-y-8 xl:space-y-8">
                            <img
                                alt="Remote Employee PH"
                                class="mx-auto w-72 xl:hidden"
                                :src="logo"
                            />

                            <div>
                                <h2
                                    class="text-2xl font-bold text-center xl:text-start xl:text-3xl"
                                >
                                    Sign In
                                </h2>

                                <div
                                    class="mx-auto mt-1 max-w-[280px] text-center text-slate-400
                                        xl:hidden"
                                >
                                    Please complete a few additional steps to
                                    sign in to your account.
                                </div>
                            </div>

                            <div>
                                <div>
                                    <FormInput
                                        id="emailChecker"
                                        ref="emailCheckerRef"
                                        v-model="
                                            data.emailChecker
                                        "
                                        class="login__input block min-w-full px-4 py-3 xl:min-w-[350px]"
                                        name="emailChecker"
                                        placeholder="Email"
                                        autocomplete="username"
                                        type="text"
                                        @keyup.enter="
                                            checkEmail
                                        "
                                        :validated-error="data.invalidEmailChecker ? 'Please enter a valid email address.' : ''"
                                    />
                                </div>
                            </div>
                            <div class="mt-4">
                                <span
                                    v-if="
                                        data.flash ===
                                        'invalidUser'
                                    "
                                    class="pt-6 ml-4 text-red-500"
                                >
                                    {{ data.flash_message }}
                                </span>
                            </div>

                            <div class="text-center xl:text-left">
                                <BaseButton
                                    id="loginBtn"
                                    type="button"
                                    class="min-w-[180px] rounded-full px-4 py-3 align-top xl:mr-3
                                        xl:mt-0 xl:w-32 xl:min-w-max xl:rounded-md"
                                    @click="checkEmail"
                                    label="Next"
                                    icon="ArrowRightIcon"
                                    :options="{
                                        iconPosition: 'right'
                                    }"
                                >
                                </BaseButton>

                                <LoginLinks v-if="isLocal" />
                            </div>
                        </div>
                    </div>
                    <!-- Email Form -->

                    <!-- Password Form -->
                    <div
                        v-if="data.emailType === 'client'"
                        class="w-full px-5 py-16 mx-8 my-auto bg-white rounded-md shadow-md dark:bg-darkmode-600 sm:w-3/4 sm:px-8 lg:w-2/4 xl:ml-20 xl:flex xl:w-auto xl:bg-transparent xl:p-0 xl:shadow-none"
                    >
                        <div class="w-full space-y-8">
                            <img
                                alt="Remote Employee PH"
                                class="mx-auto w-72 xl:hidden"
                                :src="logo"
                            />

                            <h2
                                class="text-lg font-medium text-center xl:text-start"
                            >
                                Sign in with
                                <span class="font-black">
                                    {{ data.email }}
                                </span>
                            </h2>

                            <div
                                v-if="
                                    !AuthStore.is2FAenabled
                                "
                            >
                                <FormInput
                                    type="email"
                                    name="email"
                                    autocomplete="username"
                                    :value="data.email"
                                    style="display: none;"
                                />
                                <FormInput
                                    id="password"
                                    ref="paswd"
                                    v-model="data.password"
                                    class="min-w-full px-4 py-3 xl:min-w-[350px]"
                                    name="password"
                                    type="password"
                                    :passwordToggle="true"
                                    placeholder="Password"
                                    autocomplete="current-password"
                                    @keydown.enter="handleSubmit"
                                    :validated-error="data.invalidPassword ? 'Please enter your password.' : data.invalidLogin ? 'Invalid email or password.' : ''"
                                />

                                <!-- <div
                                    v-if="
                                        data.invalidPassword
                                    "
                                    class="px-3 py-2"
                                >
                                    <span
                                        class="flex items-center justify-center gap-1 text-red-500 xl:text-start"
                                    >
                                        <Lucide
                                            icon="Asterisk"
                                            class="w-4 h-4"
                                        />
                                        <span>Please input your password.</span>
                                    </span>
                                </div>
                                <div v-if="data.invalidLogin" class="px-3 py-2">
                                    <span
                                        class="flex items-center justify-center gap-1 text-red-500 xl:text-start"
                                    >
                                        <Lucide
                                            icon="KeyRound"
                                            class="w-4 h-4"
                                            id="invalidLoginMessage"
                                        />
                                        Invalid username or password.
                                    </span>
                                </div> -->
                            </div>

                            <!-- 2Factor Authentication -->
                            <div
                                v-if="
                                    AuthStore.is2FAenabled
                                "
                                class="mt-8 intro-x"
                            >
                                <FormInput
                                    id="code"
                                    class="login__input block min-w-full px-4 py-3 xl:min-w-[350px]"
                                    :class="{
                                        'border-red-500 focus:border-red-400 focus:ring-red-600':
                                            AuthStore.invalid2FA ||
                                            data
                                                .twoFactorValidation
                                                .error
                                    }"
                                    name="code"
                                    placeholder="Two factor code"
                                    type="text"
                                />
                                <div
                                    v-if="
                                        AuthStore.invalid2FA
                                    "
                                    class="px-3 py-2"
                                >
                                    <span
                                        class="flex items-center justify-center gap-1 text-red-500 xl:text-start"
                                        id="invalidLoginMessage"
                                    >
                                        <Lucide
                                            icon="KeyRound"
                                            class="w-4 h-4"
                                        />
                                        Invalid two factor code. Please try
                                        again.
                                    </span>
                                </div>
                                <div
                                    v-if="
                                        data
                                            .twoFactorValidation
                                            .error
                                    "
                                    class="px-3 py-2"
                                >
                                    <span
                                        id="invalidLoginMessage"
                                        class="flex items-center justify-center gap-1 text-red-500 xl:text-start"
                                    >
                                        <Lucide
                                            icon="KeyRound"
                                            class="w-4 h-4"
                                        />
                                        Please enter your two factor code.
                                    </span>
                                </div>
                            </div>

                            <div
                                v-if="
                                    !AuthStore.is2FAenabled
                                "
                                class="flex flex-col items-center justify-center gap-4 text-center xl:mt-8 xl:items-start xl:text-left"
                            >
                                <BaseButton
                                    variant="primary"
                                    id="loginBtn"
                                    class="min-w-[240px] rounded-full px-4 py-3 align-top xl:mr-3
                                        xl:mt-0 xl:min-w-max xl:max-w-[200px] xl:rounded-md"
                                    @click="handleSubmit"
                                    type="button"
                                >
                                    <LoadingIcon
                                        v-if="
                                            AuthStore.invalidLogin
                                        "
                                        class="w-4 h-4"
                                        icon="oval"
                                    />
                                    <span>Login</span>
                                    <Lucide
                                        icon="KeyRound"
                                        class="w-5 h-5 ml-2"
                                    />
                                </BaseButton>

                                <BaseButton
                                    variant="secondary"
                                    kind="padless"
                                    id="backButton"
                                    class="xl:px-0"
                                    @click="
                                        changeEmailAddress
                                    "
                                >
                                    <LoadingIcon
                                        v-if="
                                            AuthStore.invalidLogin
                                        "
                                        class="w-4 h-4"
                                        icon="oval"
                                    />
                                    <Lucide icon="ArrowLeft" class="w-5 h-5" />
                                    Change Email Address
                                </BaseButton>
                            </div>
                            <div class="text-slate-400">
                                <router-link
                                    :to="{
                                        name: 'forgot-password',
                                        query: {
                                            email: data.email
                                        }
                                    }"
                                    class="flex items-center justify-center gap-2 xl:justify-start"
                                >
                                    <Lucide
                                        icon="LockKeyholeOpen"
                                        class="w-4 h-4"
                                    />
                                    <span>Forgot Password</span>
                                </router-link>
                            </div>
                        </div>
                        <div
                            class="my-auto ml-10 hidden w-full max-w-[320px] flex-shrink-0
                                xl:block"
                        >
                            <img
                                alt="Remote Employee PH"
                                class="opacity-25"
                                :src="logo"
                            />
                        </div>
                    </div>
                    <!-- Password Form -->
                </form>
                <!-- END: Login Form -->
            </div>
        </div>
    </div>
</template>
